import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Query, QueryComponentOptions } from '@apollo/client/react/components';
import { isEmpty } from 'utils/functions';
import LoadingSpinner from 'components/LoadingSpinner';
import { STILL_PROCESSING, HANG_TIGHT, UNABLE_CLICK_TO } from 'utils/strings';

const RETRYABLE_ERROR_TYPES = ['Http:ExecutionTimeoutException', 'ExecutionTimeout'];

const LOADING_STATES_WAITS = {
  1: 5000,
  2: 10000
};

type ChildrenType = {
  data: any;
  refetch: any;
};

interface Props extends QueryComponentOptions {
  children(args: ChildrenType): any;
  hideWarning?: boolean;
}

const QueryRetryWrapper: React.FC<Props> = props => {
  const [loadingState, setLoadingState] = useState(0);
  const { hideWarning } = props;
  let timer;
  useEffect(() => {
    // In some cases the component unmounts and the timer is still running
    return () => clearTimeout(timer);
  });
  const renderLoading = () => {
    switch (loadingState) {
      case 0:
        timer = setTimeout(() => {
          setLoadingState(1);
        }, LOADING_STATES_WAITS[1]);
        return <LoadingSpinner />;
      case 1:
        timer = setTimeout(() => {
          setLoadingState(2);
        }, LOADING_STATES_WAITS[2]);
        return <LoadingSpinner message={STILL_PROCESSING} />;
      default:
        return <LoadingSpinner message={HANG_TIGHT} />;
    }
  };

  return (
    <Query
      {...props}
      notifyOnNetworkStatusChange
      onCompleted={data => {
        setLoadingState(0);
        if (props.onCompleted) {
          props.onCompleted(data);
        }
      }}
      onError={error => {
        setLoadingState(0);
        if (props.onError) {
          props.onError(error);
        }
      }}
    >
      {({ error = undefined, data, networkStatus, refetch }) => {
        if (networkStatus < 7) {
          return renderLoading();
        }
        clearTimeout(timer);
        if (error) {
          const isTimeoutError: boolean =
            error.graphQLErrors.filter(graphQLError =>
              RETRYABLE_ERROR_TYPES.includes(graphQLError.errorType)
            ).length > 0;
          if (isTimeoutError) {
            return (
              <Alert variant="danger" style={{ textAlign: 'center' }}>
                {UNABLE_CLICK_TO}
                <Button
                  variant="link"
                  onClick={() => {
                    refetch().catch(error => {
                      console.debug(error);
                    });
                  }}
                  style={{ height: 'auto', display: 'contents' }}
                >
                  retry.
                </Button>
              </Alert>
            );
          }
          return (
            <Alert variant="danger">
              {process.env.NODE_ENV !== 'production'
                ? error.message
                : 'Unable to complete the request. Please contact ' +
                  (
                    <a
                      className="support-mail"
                      target="_blank"
                      rel="noopener noreferrer"
                      href="mailto:signals@destini.co"
                    >
                      Support
                    </a>
                  ) +
                  '.'}
            </Alert>
          );
        }
        if (isEmpty(data) && !hideWarning) return <Alert variant="warning">No Data Found</Alert>;
        return props.children({ data, refetch });
      }}
    </Query>
  );
};

QueryRetryWrapper.defaultProps = {
  hideWarning: false
};

export default QueryRetryWrapper;
